import type { CompositeFilterDescriptor, FilterDescriptor } from '@progress/kendo-data-query';
import type { PatternFormField, PatternFormFieldItem } from '@ricoh/ria.client.core/types/component/pattern.type';
import type { Nullable } from '@ricoh/ria.client.core/types/core/data.type';

import { isCompositeFilterDescriptor } from '@progress/kendo-data-query';
import { createLogger } from '@ricoh/ria.client.core/plugins/app/logger';
import { getStringValue, isNotNull } from '@ricoh/ria.client.core/utils/data/object';

import type { PatternAccessScope } from '~/common/logic/feature/settings/batch-type-library/pattern';
import type {
  BusinessRuleCalculateValuePattern,
  BusinessRuleCalculateValuePatternWithParameters,
  BusinessRuleQueryAzureWithParameters,
} from '~/common/model/api/batch-type/business-rule';
import type { Pattern, PatternParameter, TransformationDbSelectCondition } from '~/common/model/api/batch-type/pattern';
import type { DataTypeGeneral, DataTypeList, DataValue, DataValueNull } from '~/common/model/api/common';
import type { DatabaseTableColumn } from '~/common/model/api/database';
import type {
  BusinessRulesCalculateValueSubmitData,
  GroupFormField,
  ValueFormField,
} from '~/components/feature/settings/batch-type-library/form/business-rules/calculate-value';

import { DATA_TYPE_GENERAL_NAMES } from '~/common/constants/feature/settings/batch-type-library';
import commonLogic from '~/common/logic/common';
import fieldLogic from '~/common/logic/feature/settings/batch-type-library/field';
import patternLogic from '~/common/logic/feature/settings/batch-type-library/pattern';

const logger = createLogger('logic/feature/settings/batch-type-library');

// * Transformation to text is not necessary
const IS_TRANSFORMATION_TEXT = false;

/**
 * Create Pattern from BusinessRule FormFieldItem.
 *
 * @param {PatternFormField} formField BusinessRule FormFieldItem.
 * @param {PatternAccessScope} scope Access scope for creating pattern.
 *
 * @returns {Pattern[]} BusinessRule Pattern.
 */
export function createBusinessRulePattern(formField: PatternFormField, scope: PatternAccessScope): Pattern[] {
  return formField.reduce<Pattern[]>((prev, item) => {
    let pattern: Nullable<Pattern>;
    if (typeof item === 'string') {
      pattern = patternLogic.createPattern.fromText(item);
    } else {
      switch (item.tabKey) {
        case 'systemValue': {
          pattern = patternLogic.createPattern.fromSystemValue(item, scope, IS_TRANSFORMATION_TEXT);
          break;
        }

        case 'globalVariable': {
          pattern = patternLogic.createPattern.fromGlobalVariable(item, IS_TRANSFORMATION_TEXT);
          break;
        }

        case 'batchField': {
          pattern = patternLogic.createPattern.fromBatchField(item, scope, IS_TRANSFORMATION_TEXT);
          break;
        }

        case 'batchVariable': {
          pattern = patternLogic.createPattern.fromBatchVariable(item, scope, IS_TRANSFORMATION_TEXT);
          break;
        }

        case 'batchBusinessRule': {
          pattern = patternLogic.createPattern.fromBusinessRule(item, scope, IS_TRANSFORMATION_TEXT);
          break;
        }

        case 'documentField': {
          pattern = patternLogic.createPattern.fromDocumentField(item, IS_TRANSFORMATION_TEXT);
          break;
        }

        case 'documentVariable': {
          pattern = patternLogic.createPattern.fromDocumentVariable(item, IS_TRANSFORMATION_TEXT);
          break;
        }

        case 'documentBusinessRule': {
          pattern = patternLogic.createPattern.fromDocumentBusinessRule(item, IS_TRANSFORMATION_TEXT);
          break;
        }

        case 'parameter': {
          pattern = patternLogic.createPattern.fromParameter(item.value as string);
          break;
        }

        case 'output': {
          pattern = patternLogic.createPattern.fromOutput(item.value as string);
          break;
        }

        default: {
          pattern = undefined;
          break;
        }
      }
    }

    if (isNotNull(pattern)) {
      prev.push(pattern);
    } else {
      logger.writeWithData('warn', 'Invalid pattern item', { cause: item });
    }

    return prev;
  }, []);
}

/**
 * Create BusinessRule FormFieldItem from Pattern.
 *
 * @param {Pattern[]} businessRulePattern Pattern.
 *
 * @returns {PatternFormField} BusinessRule FormFieldItem.
 */
export function createBusinessRuleFormFieldItem(businessRulePattern: Pattern[]): PatternFormField {
  return businessRulePattern.reduce<PatternFormField>((prev, pattern) => {
    let item: Nullable<PatternFormFieldItem>;
    switch (pattern.$is) {
      case 'const': {
        item = patternLogic.createPatternFormFieldItem.fromPatternConstant(pattern);
        break;
      }

      case 'now':
      case 'guid':
      case 'random': {
        item = patternLogic.createPatternFormFieldItem.fromPatternGenerate(pattern, IS_TRANSFORMATION_TEXT);
        break;
      }

      case 'global': {
        item = patternLogic.createPatternFormFieldItem.fromPatternGlobal(pattern);
        break;
      }

      case 'batch': {
        item = patternLogic.createPatternFormFieldItem.fromPatternBatch(pattern);
        break;
      }

      case 'document': {
        item = patternLogic.createPatternFormFieldItem.fromPatternDocument(pattern);
        break;
      }

      case 'parameter': {
        item = patternLogic.createPatternFormFieldItem.fromPatternParameter(pattern);
        break;
      }

      case 'output': {
        item = patternLogic.createPatternFormFieldItem.fromPatternOutput(pattern);
        break;
      }

      default: {
        item = undefined;
        break;
      }
    }

    if (isNotNull(item)) {
      prev.push(item);
    } else {
      logger.writeWithData('warn', 'Invalid pattern', { cause: pattern });
    }

    return prev;
  }, []);
}

/**
 * Create CalculateValue.
 *
 * @param {BusinessRulesCalculateValueSubmitData['pattern']} pattern Pattern.
 * @param {PatternAccessScope} scope Access scope for creating pattern.
 *
 * @returns {BusinessRuleCalculateValuePattern} Create CalculateValue.
 */
export function createCalculateValue(
  pattern: BusinessRulesCalculateValueSubmitData['pattern'],
  scope: PatternAccessScope,
): BusinessRuleCalculateValuePatternWithParameters {
  const parameters: Record<string, PatternParameter> = {};

  return {
    pattern: (function build(sourcePattern: ValueFormField | GroupFormField): BusinessRuleCalculateValuePattern {
      if ('items' in sourcePattern) {
        return {
          operator: sourcePattern.operator,
          items: sourcePattern.items?.map((item) => build(item)),
        };
      }

      const [value] = sourcePattern.value;

      if (sourcePattern && typeof value === 'object' && 'tabKey' in value && value.tabKey === 'parameter') {
        parameters[`${value.value}`] = {
          $is: 'parameter',
          name: (value.value as string) ?? '',
        };
      }

      return {
        operator: getStringValue(sourcePattern.operator),
        param: createBusinessRulePattern(sourcePattern.value ?? [], scope),
      };
    })(pattern) ?? {
      operator: 'add',
      items: [],
    },
    parameters,
  };
}

/**
 * Create CalculateValue FormField.
 *
 * @param {BusinessRuleCalculateValuePattern} pattern Pattern.
 *
 * @returns {BusinessRulesCalculateValueSubmitData['pattern']} Create CalculateValue FormField.
 */
export function createCalculateValueFormField(pattern: BusinessRuleCalculateValuePattern): BusinessRulesCalculateValueSubmitData['pattern'] {
  const calc = (function build(sourcePattern: BusinessRuleCalculateValuePattern): ValueFormField | GroupFormField {
    if ('param' in sourcePattern) {
      const { operator, param } = sourcePattern;
      return {
        operator,
        value: createBusinessRuleFormFieldItem(param),
      };
    }

    return {
      operator: sourcePattern.operator,
      items: sourcePattern.items?.map((item) => build(item)) ?? [],
    };
  })(pattern);

  if ('items' in calc) {
    return calc;
  }

  return {
    operator: 'add',
    items: [calc],
  };
}

/**
 * Create Condition FormField.
 *
 * @param {TransformationDbSelectCondition} condition Condition.
 *
 * @returns {CompositeFilterDescriptor} Condition FormField.
 */
export function createConditionFormField(condition: TransformationDbSelectCondition): CompositeFilterDescriptor {
  const createValue = (fieldValue: DataValue | DataValueNull | undefined): DataValue['value'] => {
    if (!fieldValue?.type) {
      return null;
    }

    const { type, value } = fieldValue;

    if (DATA_TYPE_GENERAL_NAMES.includes(type)) {
      return value;
    }

    const listFieldType = commonLogic.type.getListDataType(type);
    if (listFieldType && Array.isArray(value)) {
      return value;
    }

    return null;
  };

  const filter = (function build(source: TransformationDbSelectCondition): FilterDescriptor | CompositeFilterDescriptor {
    if ('group' in source) {
      return {
        logic: source.group,
        filters: source.rules?.map(build) ?? [],
      };
    }

    const { field, operator, value } = source;

    return {
      field,
      operator,
      value: operator === 'in' || operator === 'notIn' ? createValue(value as DataValue | DataValueNull) : createBusinessRuleFormFieldItem(value as Pattern[]),
    };
  })(condition);

  if (isCompositeFilterDescriptor(filter)) {
    return filter;
  }

  return {
    logic: 'and',
    filters: [filter],
  };
}

/**
 * Create Condition from Condition FormField.
 *
 * @param {CompositeFilterDescriptor} conditionFormField Condition FormField.
 * @param {DatabaseTableColumn} columns Columns.
 *
 * @returns {TransformationDbSelectCondition} Condition.
 */
export function createCondition(conditionFormField: CompositeFilterDescriptor, columns: DatabaseTableColumn[]): BusinessRuleQueryAzureWithParameters {
  const parameters: Record<string, PatternParameter> = {};

  return {
    pattern: (function build(descriptor: FilterDescriptor | CompositeFilterDescriptor): Nullable<TransformationDbSelectCondition> {
      if (isCompositeFilterDescriptor(descriptor)) {
        return {
          group: descriptor.logic,
          rules: descriptor.filters.map((filter) => build(filter)).filter((filter): filter is TransformationDbSelectCondition => !!filter),
        };
      }

      const [value] = descriptor.value;

      if (descriptor && typeof value === 'object' && 'tabKey' in value && value.tabKey === 'parameter') {
        parameters[`${value.value}`] = {
          $is: 'parameter',
          name: (value.value as string) ?? '',
        };
      }

      const column = columns.find(({ name }) => name.value === descriptor.field);
      if (!column) {
        logger.writeWithData('warn', 'Not found column', descriptor);
        return undefined;
      }

      if (column.type.value === 'table') {
        logger.writeWithData('warn', 'Invalid type', descriptor);
        return undefined;
      }

      let columnType: DataTypeGeneral | DataTypeList;

      if (column.type.value.includes('list.')) {
        columnType = {
          name: column.type.value,
        } as DataTypeList;
      } else {
        columnType = {
          name: column.type.value,
        } as DataTypeGeneral;
      }

      return {
        field: getStringValue(descriptor.field) ?? '',
        operator: getStringValue(descriptor.operator),
        value:
          getStringValue(descriptor.operator) === 'in' || getStringValue(descriptor.operator) === 'notIn'
            ? fieldLogic.createDataValue(columnType, descriptor.value)
            : createBusinessRulePattern(descriptor.value, 'batch'),
      };
    })(conditionFormField) ?? {
      group: 'and',
      rules: [],
    },
    parameters,
  };
}

/**
 * Create Params Data.
 *
 * @param {string[]} items Items name.
 * @param {string} name Name.
 *
 * @returns {string[]} Params Name.
 */
export function createParamsData(items: string[], name: string) {
  if (items.includes(name)) {
    return items;
  }

  return [...items, name];
}
